import { useEffect, useMemo, useState } from "react";
import {
  api,
  formatCurrency,
  formatDate,
  formatNumber,
  downloadCSV,
  downloadPDF,
} from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FileCsv,
  FilePdf,
  MapPin,
} from "@phosphor-icons/react";

export default function ReportsPage() {
  const [tab, setTab] = useState("stock");
  const [stock, setStock] = useState(null);
  const [movements, setMovements] = useState(null);
  const [sites, setSites] = useState([]);

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [site, setSite] = useState("all");

  useEffect(() => {
    api
      .get("/reports/stock")
      .then(({ data }) => setStock(data))
      .catch(() =>
        setStock({
          rows: [],
          total_items: 0,
          total_value: 0,
        })
      );
  }, []);

  const loadMoves = () => {
    const params = {};

    if (dateFrom) {
      params.date_from = dateFrom;
    }

    if (dateTo) {
      params.date_to = dateTo;
    }

    api
      .get("/reports/movements", { params })
      .then(({ data }) => setMovements(data))
      .catch(() =>
        setMovements({
          rows: [],
          total_in: 0,
          total_out: 0,
        })
      );
  };

  useEffect(() => {
    loadMoves();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  /*
   * Carica l'elenco completo dei cantieri da tutti gli scarichi,
   * indipendentemente dal periodo selezionato nel report.
   * Nello scarico il cantiere è salvato nel campo "reference".
   */
  useEffect(() => {
    api
      .get("/movements", {
        params: {
          type: "out",
        },
      })
      .then(({ data }) => {
        const rows = Array.isArray(data) ? data : [];

        const names = rows
          .map((movement) =>
            String(movement.reference || "").trim()
          )
          .filter(Boolean);

        const uniqueSites = [...new Set(names)].sort(
          (a, b) =>
            a.localeCompare(b, "it", {
              sensitivity: "base",
            })
        );

        setSites(uniqueSites);
      })
      .catch(() => setSites([]));
  }, []);

  /*
   * Se viene selezionato un cantiere:
   * - mostriamo soltanto gli scarichi;
   * - il campo reference deve corrispondere al cantiere.
   */
  const filteredMovementRows = useMemo(() => {
    const rows = movements?.rows || [];

    if (site === "all") {
      return rows;
    }

    return rows.filter(
      (movement) =>
        movement.type === "out" &&
        String(movement.reference || "").trim() === site
    );
  }, [movements, site]);

  const movementTotals = useMemo(() => {
    return filteredMovementRows.reduce(
      (totals, movement) => {
        const quantity =
          Number(movement.quantity) || 0;

        if (movement.type === "in") {
          totals.totalIn += quantity;
        } else {
          totals.totalOut += quantity;
        }

        return totals;
      },
      {
        totalIn: 0,
        totalOut: 0,
      }
    );
  }, [filteredMovementRows]);

  const filteredMovementValue = useMemo(() => {
    return filteredMovementRows.reduce(
      (total, movement) =>
        total + (Number(movement.total_price) || 0),
      0
    );
  }, [filteredMovementRows]);

  const safeSiteName = (value) =>
    String(value || "")
      .trim()
      .replace(/[\\/:*?"<>|]+/g, "-")
      .replace(/\s+/g, "_");

  const movementFileSuffix =
    site === "all"
      ? ""
      : `_${safeSiteName(site)}`;

  const exportStockCSV = () => {
    if (!stock) return;

    downloadCSV(
      "giacenze.csv",
      stock.rows.map((row) => ({
        SKU: row.sku,
        Nome: row.name,
        Categoria: row.category,
        Fornitore: row.supplier,
        Unità: row.unit,
        Quantità: row.quantity,
        ScortaMin: row.min_stock,
        Prezzo: row.price,
        Valore: Number(row.value || 0).toFixed(2),
        SottoSoglia: row.low ? "SI" : "NO",
      }))
    );
  };

  const exportStockPDF = () => {
    if (!stock) return;

    downloadPDF(
      "Report giacenze",
      [
        { key: "sku", label: "SKU" },
        { key: "name", label: "Prodotto" },
        { key: "category", label: "Categoria" },
        { key: "supplier", label: "Fornitore" },
        { key: "quantity", label: "Qtà" },
        { key: "unit", label: "Um" },
        { key: "min_stock", label: "Min" },
        { key: "price_f", label: "Prezzo" },
        { key: "value_f", label: "Valore" },
      ],
      stock.rows.map((row) => ({
        ...row,
        price_f: formatCurrency(row.price),
        value_f: formatCurrency(row.value),
      }))
    );
  };

  const exportMovesCSV = () => {
    if (!movements) return;

    downloadCSV(
      `movimenti${movementFileSuffix}.csv`,
      filteredMovementRows.map((row) => ({
        Data: formatDate(row.date),
        Tipo:
          row.type === "in"
            ? "Carico"
            : "Scarico",
        SKU: row.sku,
        Prodotto: row.product,
        Quantità: row.quantity,
        Prezzo_unitario: Number(row.unit_price || 0).toFixed(2),
        Prezzo_totale: Number(row.total_price || 0).toFixed(2),
        Cantiere:
          row.type === "out"
            ? row.reference || ""
            : "",
        Riferimento_DDT:
          row.type === "in"
            ? row.reference || ""
            : "",
        Operatore: row.user,
        Note: row.note,
      }))
    );
  };

  const exportMovesPDF = () => {
    if (!movements) return;

    const title =
      site === "all"
        ? "Report movimenti"
        : `Report scarichi - ${site}`;

    downloadPDF(
      title,
      [
        { key: "date_f", label: "Data" },
        { key: "type_f", label: "Tipo" },
        { key: "sku", label: "SKU" },
        { key: "product", label: "Prodotto" },
        { key: "quantity", label: "Qtà" },
        { key: "unit_price_f", label: "Prezzo unit." },
        { key: "total_price_f", label: "Totale" },
        {
          key: "reference_f",
          label:
            site === "all"
              ? "DDT / Cantiere"
              : "Cantiere",
        },
        { key: "user", label: "Operatore" },
      ],
      filteredMovementRows.map((row) => ({
        ...row,
        date_f: formatDate(row.date),
        type_f:
          row.type === "in"
            ? "Carico"
            : "Scarico",
        reference_f: row.reference || "—",
        unit_price_f: formatCurrency(row.unit_price || 0),
        total_price_f: formatCurrency(row.total_price || 0),
      }))
    );
  };

  return (
    <Tabs value={tab} onValueChange={setTab}>
      <TabsList className="mb-6 h-10 bg-slate-100">
        <TabsTrigger
          value="stock"
          data-testid="tab-report-stock"
          className="data-[state=active]:bg-slate-900 data-[state=active]:text-white"
        >
          Giacenze
        </TabsTrigger>

        <TabsTrigger
          value="movements"
          data-testid="tab-report-movements"
          className="data-[state=active]:bg-slate-900 data-[state=active]:text-white"
        >
          Movimenti
        </TabsTrigger>
      </TabsList>

      {/* REPORT GIACENZE */}
      <TabsContent
        value="stock"
        className="space-y-4"
      >
        <Card className="flex flex-col gap-3 rounded-2xl border-slate-200 p-5 shadow-sm md:flex-row md:items-center md:justify-between">
          <div>
            <div className="text-lg font-bold tracking-tight">
              Giacenze attuali
            </div>

            <div className="text-sm text-slate-600 tabular">
              {stock
                ? `${formatNumber(
                    stock.total_items
                  )} articoli · Valore totale ${formatCurrency(
                    stock.total_value
                  )}`
                : "…"}
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={exportStockCSV}
              data-testid="export-stock-csv"
            >
              <FileCsv
                weight="bold"
                size={16}
                className="mr-1.5"
              />
              CSV
            </Button>

            <Button
              onClick={exportStockPDF}
              data-testid="export-stock-pdf"
              className="bg-slate-900 text-white hover:bg-slate-800"
            >
              <FilePdf
                weight="bold"
                size={16}
                className="mr-1.5"
              />
              PDF
            </Button>
          </div>
        </Card>

        <Card className="overflow-hidden rounded-2xl border-slate-200 shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-widest text-slate-500">
                  <th className="px-5 py-3 font-medium">
                    SKU
                  </th>
                  <th className="px-5 py-3 font-medium">
                    Prodotto
                  </th>
                  <th className="px-5 py-3 font-medium">
                    Categoria
                  </th>
                  <th className="px-5 py-3 font-medium">
                    Fornitore
                  </th>
                  <th className="px-5 py-3 text-right font-medium">
                    Qtà
                  </th>
                  <th className="px-5 py-3 text-right font-medium">
                    Prezzo
                  </th>
                  <th className="px-5 py-3 text-right font-medium">
                    Valore
                  </th>
                  <th className="px-5 py-3 text-center font-medium">
                    Stato
                  </th>
                </tr>
              </thead>

              <tbody>
                {(!stock ||
                  stock.rows.length === 0) && (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-5 py-10 text-center text-slate-500"
                    >
                      Nessun dato.
                    </td>
                  </tr>
                )}

                {stock?.rows.map((row) => (
                  <tr
                    key={row.sku}
                    className="border-t border-slate-100 hover:bg-slate-50"
                  >
                    <td className="px-5 py-2.5 text-slate-600 tabular">
                      {row.sku}
                    </td>

                    <td className="px-5 py-2.5 font-medium">
                      {row.name}
                    </td>

                    <td className="px-5 py-2.5 text-slate-600">
                      {row.category || "—"}
                    </td>

                    <td className="px-5 py-2.5 text-slate-600">
                      {row.supplier || "—"}
                    </td>

                    <td className="px-5 py-2.5 text-right tabular">
                      {row.quantity} {row.unit}
                    </td>

                    <td className="px-5 py-2.5 text-right tabular">
                      {formatCurrency(row.price)}
                    </td>

                    <td className="px-5 py-2.5 text-right font-medium tabular">
                      {formatCurrency(row.value)}
                    </td>

                    <td className="px-5 py-2.5 text-center">
                      {row.low ? (
                        <span className="rounded bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
                          Sotto soglia
                        </span>
                      ) : (
                        <span className="rounded bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                          Ok
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </TabsContent>

      {/* REPORT MOVIMENTI */}
      <TabsContent
        value="movements"
        className="space-y-4"
      >
        <Card className="flex flex-col gap-4 rounded-2xl border-slate-200 p-5 shadow-sm xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center">
            <div>
              <div className="text-lg font-bold tracking-tight">
                {site === "all"
                  ? "Movimenti per periodo"
                  : `Scarichi cantiere: ${site}`}
              </div>

              <div className="text-sm text-slate-600 tabular">
                {movements
                  ? site === "all"
                    ? `Totale carico ${formatNumber(
                        movementTotals.totalIn
                      )} · scarico ${formatNumber(
                        movementTotals.totalOut
                      )} · valore movimentato ${formatCurrency(
                        filteredMovementValue
                      )}`
                    : `Totale scaricato ${formatNumber(
                        movementTotals.totalOut
                      )} · valore ${formatCurrency(
                        filteredMovementValue
                      )}`
                  : "…"}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 xl:ml-4">
              <Input
                type="date"
                value={dateFrom}
                onChange={(event) =>
                  setDateFrom(event.target.value)
                }
                className="h-10 w-[155px]"
                data-testid="report-date-from"
              />

              <Input
                type="date"
                value={dateTo}
                onChange={(event) =>
                  setDateTo(event.target.value)
                }
                className="h-10 w-[155px]"
                data-testid="report-date-to"
              />

              <Select
                value={site}
                onValueChange={setSite}
              >
                <SelectTrigger
                  className="h-10 w-[260px] bg-white"
                  data-testid="report-site-filter"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <MapPin
                      size={17}
                      weight="bold"
                      className="shrink-0 text-slate-500"
                    />
                    <SelectValue placeholder="Seleziona cantiere" />
                  </div>
                </SelectTrigger>

                <SelectContent className="max-h-72 bg-white">
                  <SelectItem value="all">
                    Tutti i movimenti
                  </SelectItem>

                  {sites.map((siteName) => (
                    <SelectItem
                      key={siteName}
                      value={siteName}
                    >
                      {siteName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={exportMovesCSV}
              disabled={
                !movements ||
                filteredMovementRows.length === 0
              }
              data-testid="export-moves-csv"
            >
              <FileCsv
                weight="bold"
                size={16}
                className="mr-1.5"
              />
              CSV
            </Button>

            <Button
              onClick={exportMovesPDF}
              disabled={
                !movements ||
                filteredMovementRows.length === 0
              }
              data-testid="export-moves-pdf"
              className="bg-slate-900 text-white hover:bg-slate-800"
            >
              <FilePdf
                weight="bold"
                size={16}
                className="mr-1.5"
              />
              PDF
            </Button>
          </div>
        </Card>

        <Card className="overflow-hidden rounded-2xl border-slate-200 shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-widest text-slate-500">
                  <th className="px-5 py-3 font-medium">
                    Data
                  </th>
                  <th className="px-5 py-3 font-medium">
                    Tipo
                  </th>
                  <th className="px-5 py-3 font-medium">
                    SKU
                  </th>
                  <th className="px-5 py-3 font-medium">
                    Prodotto
                  </th>
                  <th className="px-5 py-3 text-right font-medium">
                    Qtà
                  </th>
                  <th className="px-5 py-3 text-right font-medium">
                    Prezzo unit.
                  </th>
                  <th className="px-5 py-3 text-right font-medium">
                    Totale
                  </th>
                  <th className="px-5 py-3 font-medium">
                    {site === "all"
                      ? "DDT / Cantiere"
                      : "Cantiere"}
                  </th>
                  <th className="px-5 py-3 font-medium">
                    Operatore
                  </th>
                </tr>
              </thead>

              <tbody>
                {filteredMovementRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-5 py-10 text-center text-slate-500"
                    >
                      {site === "all"
                        ? "Nessun movimento nel periodo."
                        : "Nessuno scarico registrato per il cantiere selezionato."}
                    </td>
                  </tr>
                )}

                {filteredMovementRows.map(
                  (row, index) => (
                    <tr
                      key={`${row.date}-${row.sku}-${index}`}
                      className="border-t border-slate-100 hover:bg-slate-50"
                    >
                      <td className="whitespace-nowrap px-5 py-2.5 text-slate-600 tabular">
                        {formatDate(row.date)}
                      </td>

                      <td className="px-5 py-2.5">
                        <span
                          className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium ${
                            row.type === "in"
                              ? "bg-green-50 text-green-700"
                              : "bg-orange-50 text-orange-700"
                          }`}
                        >
                          {row.type === "in"
                            ? "Carico"
                            : "Scarico"}
                        </span>
                      </td>

                      <td className="px-5 py-2.5 text-slate-600 tabular">
                        {row.sku}
                      </td>

                      <td className="px-5 py-2.5 font-medium">
                        {row.product}
                      </td>

                      <td className="px-5 py-2.5 text-right font-medium tabular">
                        {row.quantity}
                      </td>

                      <td className="px-5 py-2.5 text-right text-slate-600 tabular">
                        {formatCurrency(row.unit_price || 0)}
                      </td>

                      <td className="px-5 py-2.5 text-right font-semibold text-slate-900 tabular">
                        {formatCurrency(row.total_price || 0)}
                      </td>

                      <td className="px-5 py-2.5 text-slate-600">
                        {row.reference || "—"}
                      </td>

                      <td className="max-w-[180px] truncate px-5 py-2.5 text-slate-600">
                        {row.user || "—"}
                      </td>
                    </tr>
                  )
                )}
                {filteredMovementRows.length > 0 && (
                  <tr className="border-t-2 border-slate-200 bg-slate-50">
                    <td
                      colSpan={6}
                      className="px-5 py-3 text-right text-sm font-bold text-slate-700"
                    >
                      Totale movimenti filtrati
                    </td>
                    <td className="px-5 py-3 text-right text-base font-bold text-slate-950 tabular">
                      {formatCurrency(filteredMovementValue)}
                    </td>
                    <td colSpan={2} />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </TabsContent>
    </Tabs>
  );
}